import classnames from 'classnames';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { connect } from 'react-redux';
import { Field } from 'redux-form';
import CheckboxField from 'shared-components/reduxForms/fields/CheckboxField/CheckboxField';
import TextAreaField from 'shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import { CATERING_PACKAGE_FORM } from '../../app-main/components/reduxForms/constants/formNames';
import RadioField from '../../app-main/components/reduxForms/fields/RadioField/RadioField';
import SelectField from '../../shared-components/reduxForms/fields/SelectField/SelectField';

const CateringPackageFormCmp = ({
  cateringPackage,
  onIsValidChange,
  formValues
}) => {
  const [isValid, setIsValid] = useState(false);

  const validateForm = () => {
    const menuSections = cateringPackage.menu.menu_sections;

    const _isValid = menuSections.every((section) => {
      if (section.max_selections > 1) {
        const selectedItems = Object.entries(
          formValues?.selectedItems?.[section.id] || {}
        ).filter((key) => key[1]?.isSelected);

        const selectedItemsData = (selectedItems || []).map((item) => {
          const itemName = item[0];
          const itemData = (section?.items || []).find(
            ({ name }) => name === itemName
          );
          return itemData;
        });

        const itemsValid = (selectedItemsData || []).every((item) => {
          const itemSizeOptions = item?.sizes;

          // if there are no size options, we don't need to validate the size
          if (!itemSizeOptions?.length) {
            return true;
          }

          const selectedSize = get(
            formValues,
            `selectedItems.${section.id}.${item.name}.selectedSize`
          );

          return !!selectedSize;
        });

        return (
          itemsValid &&
          selectedItems.length <= section.max_selections &&
          selectedItems.length >= section.min_selections
        );
      }

      // this is for radio fields
      if (section.max_selections === 1 && section.min_selections > 0) {
        return !!formValues?.selectedItems?.[section.id];
      }

      return true;
    });

    if (_isValid !== isValid) {
      onIsValidChange(_isValid);
      setIsValid(_isValid);
    }
  };

  useEffect(() => {
    validateForm();
  }, [formValues]);

  const getRadioField = (menuSection) => {
    return (
      <div className="catering-form-radio-wrap">
        <Field
          name={`selectedItems.${menuSection.id}`}
          component={RadioField}
          options={menuSection.items.map((menuItem) => ({
            id: menuItem.name,
            value: menuItem.name,
            label: (
              <span>
                {menuItem.name}
                {menuItem.price?.value > 0 && (
                  <span className="text-gray-600">
                    {' '}
                    (
                    <FormattedNumber
                      value={menuItem.price?.value}
                      currency="USD"
                      style="currency"
                      minimumFractionDigits={
                        Number.isInteger(menuItem.price?.value) ? 0 : 2
                      }
                      maximumFractionDigits={2}
                    />
                    {menuItem.price?.per_person && ' per person'})
                  </span>
                )}
              </span>
            )
          }))}
        />
      </div>
    );
  };

  const getCheckboxFields = (menuSection) => {
    const checkboxField = (menuItem, fieldName) => {
      const isSelected = get(formValues, `${fieldName}.isSelected`, false);
      const totalSelected = Object.entries(
        formValues?.selectedItems?.[menuSection.id] || {}
      ).filter((key) => key[1]?.isSelected).length;

      const shouldDisable =
        !isSelected && totalSelected >= menuSection.max_selections;

      return (
        <Field
          key={`${fieldName}.isSelected`}
          name={`${fieldName}.isSelected`}
          component={CheckboxField}
          id={menuItem.name}
          attributes={{
            disabled: shouldDisable,
            checked: isSelected
          }}
          label={
            <span>
              {menuItem.name}
              {menuItem.price?.value > 0 && (
                <span className="text-gray-600">
                  {' '}
                  (
                  <FormattedNumber
                    value={menuItem.price?.value}
                    currency="USD"
                    style="currency"
                    minimumFractionDigits={
                      Number.isInteger(menuItem.price?.value) ? 0 : 2
                    }
                    maximumFractionDigits={2}
                  />
                  {menuItem.price?.per_person && ' per person'})
                </span>
              )}
            </span>
          }
          value={menuItem.name}
          option={menuItem.name}
        />
      );
    };

    const checkboxSelectField = (menuItem, fieldName) => {
      const isSelected = get(formValues, `${fieldName}.isSelected`, false);

      return (
        <div
          className={classnames(
            'catering-form-checkbox-select-field flex column',
            { 'is-selected': isSelected }
          )}
        >
          <span className="tw-text-body-2-m">
            {checkboxField(menuItem, fieldName)}
          </span>
          <div className="catering-form-size-select-wrap margin-sm-top">
            {menuItem.description && (
              <p className="no-margin">{menuItem.description}</p>
            )}
            <div className="margin-md-top">
              <Field
                id={`${fieldName}.selectedSize`}
                name={`${fieldName}.selectedSize`}
                component={SelectField}
                menuShouldScrollIntoView={false}
                placeholder="Select size"
                options={menuItem.sizes.map((size) => {
                  return {
                    value: size.name,
                    label: (
                      <span>
                        {size.name}
                        {size.price?.value > 0 && (
                          <span className="text-gray-600">
                            {' '}
                            ({' '}
                            <FormattedNumber
                              value={size.price?.value}
                              currency="USD"
                              style="currency"
                              minimumFractionDigits={
                                Number.isInteger(size.price?.value) ? 0 : 2
                              }
                              maximumFractionDigits={2}
                            />
                            {size.price?.per_person && ' per person'})
                          </span>
                        )}
                      </span>
                    )
                  };
                })}
                isDisabled={!isSelected}
              />
            </div>
          </div>
        </div>
      );
    };

    return !menuSection.items?.[0]?.sizes?.length ? (
      <div className="catering-form-checkbox-wrap">
        {menuSection.items.map((menuItem) => {
          const fieldName = `selectedItems.${menuSection.id}.${menuItem.name}`;
          return checkboxField(menuItem, fieldName);
        })}
      </div>
    ) : (
      <div className="catering-form-checkbox-select-wrap">
        {menuSection.items.map((menuItem) => {
          const fieldName = `selectedItems.${menuSection.id}.${menuItem.name}`;
          return checkboxSelectField(menuItem, fieldName);
        })}
      </div>
    );
  };

  return (
    <form className="catering-menu-form">
      <div className="catering-menu-form-inner">
        {cateringPackage.menu.menu_sections.map((section, i) => {
          return (
            <div key={section.title}>
              <span className="tw-body-2-m">{section.title}</span>
              {section.description && (
                <p className="tw-body-2 no-margin">{section.description}</p>
              )}

              {!!section.items?.length && (
                <div className="margin-sm-top">
                  {section.max_selections > 1
                    ? getCheckboxFields(section)
                    : getRadioField(section)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <hr style={{ margin: '32px 0' }} />

      <div>
        <div className="margin-md-bottom">
          <label htmlFor="catering-notes">Special requests</label>
          <p className="text-gray-600 no-margin">
            Please list any allergens, dietary restrictions or preferences, etc.
          </p>
        </div>

        <Field name="cateringNotes" component={TextAreaField} />
      </div>
    </form>
  );
};

const mapStateToProps = ({ form }) => ({
  formValues: form[CATERING_PACKAGE_FORM]?.values
});

export const CateringPackageForm = connect(mapStateToProps)(
  CateringPackageFormCmp
);
